import React from 'react';
import { useMsal } from '@azure/msal-react';
import { acquireApiAccessTokenAsync } from '../../../services/auth/AuthService';
import { useProviderServices } from '../../../app/providers/AppServicesContext';
import { selectSelectedSession } from '../../../domain/selectors/selected/chat/selectSelectedSession';
import { alerts } from '../../../shared/ui/Alerts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import {
    removeSavedPrompt,
    upsertSavedPrompt,
} from '../../../redux/features/chatPersonalization/chatPersonalizationSlice';
import { ChatProviderType } from '../../../domain/chat/models/ChatProviderType';
import { selectSavedPrompts } from '../../../domain/selectors/base/chatPersonalization/selectSavedPrompts';

export interface UserPromptOption {
    id: string;
    text: string;
}

export interface UseSavedPromptVm {
    userPromptOptions: UserPromptOption[];
    isPromptDialogOpen: boolean;
    isDeletePromptDialogOpen: boolean;
    promptDialogValue: string;
    editingPrompt: UserPromptOption | null;
    promptPendingDelete: UserPromptOption | null;
    isPromptSaving: boolean;
    openSavePromptDialog: () => void;
    openEditPromptDialog: (prompt: UserPromptOption) => void;
    closePromptDialog: () => void;
    setPromptDialogValue: (value: string) => void;
    savePrompt: () => Promise<void>;
    openDeletePromptDialog: (prompt: UserPromptOption) => void;
    closeDeletePromptDialog: () => void;
    deletePrompt: () => Promise<void>;
}

export const useSavedPrompt = (): UseSavedPromptVm => {
    const dispatch = useAppDispatch();
    const providerServices = useProviderServices();
    const { instance } = useMsal();
    const session = useAppSelector(selectSelectedSession);
    const selectedSessionId = useAppSelector((state) => state.chatUi.selectedSessionId);
    const draftInput = useAppSelector((state) =>
        selectedSessionId ? (state.chatUi.bySessionId[selectedSessionId]?.draftInput ?? '') : '',
    );
    const savedPrompts = useAppSelector(selectSavedPrompts);

    const sortedSavedPrompts = React.useMemo(
        () => [...savedPrompts].sort((a, b) => (b.modifiedDate ?? b.createdDate) - (a.modifiedDate ?? a.createdDate)),
        [savedPrompts],
    );


    const userPromptOptions = React.useMemo<UserPromptOption[]>(() => {
        if (!session) {
            return sortedSavedPrompts.map((prompt) => ({ id: prompt.id, text: prompt.text }));
        }

        switch (session.providerType) {
            case ChatProviderType.ProGPT:
                return sortedSavedPrompts.map((prompt) => ({ id: prompt.id, text: prompt.text }));
            case ChatProviderType.Nexus:
                return sortedSavedPrompts
                    .filter((prompt) => prompt.agentId === session.agentId)
                    .map((prompt) => ({ id: prompt.id, text: prompt.text }));
            default:
                return [];
        }
    }, [session, sortedSavedPrompts]);

    const [isPromptDialogOpen, setIsPromptDialogOpen] = React.useState(false);
    const [isDeletePromptDialogOpen, setIsDeletePromptDialogOpen] = React.useState(false);
    const [promptDialogValue, setPromptDialogValueState] = React.useState('');
    const [editingPrompt, setEditingPrompt] = React.useState<UserPromptOption | null>(null);
    const [promptPendingDelete, setPromptPendingDelete] = React.useState<UserPromptOption | null>(null);
    const [isPromptSaving, setIsPromptSaving] = React.useState(false);

    const openSavePromptDialog = React.useCallback((): void => {
        setEditingPrompt(null);
        setPromptDialogValueState(draftInput);
        setIsPromptDialogOpen(true);
    }, [draftInput]);

    const openEditPromptDialog = React.useCallback((prompt: UserPromptOption): void => {
        setEditingPrompt(prompt);
        setPromptDialogValueState(prompt.text);
        setIsPromptDialogOpen(true);
    }, []);

    const closePromptDialog = React.useCallback((): void => {
        if (isPromptSaving) {
            return;
        }

        setIsPromptDialogOpen(false);
        setPromptDialogValueState('');
        setEditingPrompt(null);
    }, [isPromptSaving]);

    const openDeletePromptDialog = React.useCallback((prompt: UserPromptOption): void => {
        setPromptPendingDelete(prompt);
        setIsDeletePromptDialogOpen(true);
    }, []);

    const closeDeletePromptDialog = React.useCallback((): void => {
        if (isPromptSaving) {
            return;
        }

        setIsDeletePromptDialogOpen(false);
        setPromptPendingDelete(null);
    }, [isPromptSaving]);

    const setPromptDialogValue = React.useCallback((value: string): void => {
        setPromptDialogValueState(value);
    }, []);

    const savePrompt = React.useCallback(async (): Promise<void> => {
        if (!session) {
            alerts.error('Please select a supported chat before saving a prompt.');
            return;
        }

        const trimmedValue = promptDialogValue.trim();
        if (!trimmedValue) {
            alerts.error('Prompt text cannot be empty.');
            return;
        }

        setIsPromptSaving(true);

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            const agentId = session.agentId;

            const savedPrompt = editingPrompt
                ? await providerServices.proGPT.personalization.editPromptAsync(
                      editingPrompt.id,
                      {
                          text: trimmedValue,
                          agentId,
                      },
                      accessToken,
                  )
                : await providerServices.proGPT.personalization.createPromptAsync(
                      {
                          text: trimmedValue,
                          agentId,
                      },
                      accessToken,
                  );

            dispatch(upsertSavedPrompt(savedPrompt));
            alerts.success(editingPrompt ? 'Saved prompt updated successfully.' : 'Saved prompt created successfully.');
            closePromptDialog();
        } catch {
            alerts.error(editingPrompt ? 'Failed to update saved prompt.' : 'Failed to save prompt.');
        } finally {
            setIsPromptSaving(false);
        }
    }, [closePromptDialog, dispatch, editingPrompt, instance, promptDialogValue, providerServices, session]);

    const deletePrompt = React.useCallback(async (): Promise<void> => {
        if (!promptPendingDelete) {
            return;
        }

        setIsPromptSaving(true);

        try {
            const accessToken = await acquireApiAccessTokenAsync({
                msalInstance: instance,
            });

            await providerServices.proGPT.personalization.deletePromptAsync(promptPendingDelete.id, accessToken);

            dispatch(removeSavedPrompt(promptPendingDelete.id));
            alerts.success('Saved prompt deleted successfully.');
            closeDeletePromptDialog();
        } catch {
            alerts.error('Failed to delete saved prompt.');
        } finally {
            setIsPromptSaving(false);
        }
    }, [closeDeletePromptDialog, dispatch, instance, promptPendingDelete, providerServices]);

    return {
        userPromptOptions,
        isPromptDialogOpen,
        isDeletePromptDialogOpen,
        promptDialogValue,
        editingPrompt,
        promptPendingDelete,
        isPromptSaving,
        openSavePromptDialog,
        openEditPromptDialog,
        closePromptDialog,
        setPromptDialogValue,
        savePrompt,
        openDeletePromptDialog,
        closeDeletePromptDialog,
        deletePrompt,
    };
};

export default useSavedPrompt;
